import { select } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Store } from '@ngneat/elf';
import { dateUtils } from '@/utils/date.utils';
import { BaseStoreRepository } from '@/store/base-store.repository';
import { PpmMilestoneProps, ppmMilestonesInitialState } from './milestones.store';
import { PpmMilestone } from '@/types';

export type PpmMileStoneStore = Store<
  {
    name: string;
    state: PpmMilestoneProps;
    config: undefined;
  },
  PpmMilestoneProps
>;

@Injectable({ providedIn: 'root' })
export class PpmMilestonesRepositoryFactory {
  static create(store: PpmMileStoneStore) {
    return new PpmMilestoneRepository(store);
  }
}

export class PpmMilestoneRepository extends BaseStoreRepository<PpmMilestoneProps> {
  constructor(store: PpmMileStoneStore) {
    super(store.pipe(select(state => state)), store, { ...ppmMilestonesInitialState });
  }

  setMilestoneDto(response: PpmMilestone): void {
    this.set('milestone', response);
    this.set('milestoneDto', {
      id: response.id,
      realisationEnvisagees: response.realisationEnvisagees,
      'service.id': response.service?.id,
      beneficiaire: response.beneficiaire || '',
      'typeMarcher.id': response.typeMarcher?.id,
      'modePassation.id': response.modePassation?.id,
      datePrevLps: dateUtils.normalizeDate(new Date(response.datePrevLps)),
      datePrevAc: dateUtils.normalizeDate(new Date(response.datePrevAc)),
      datePrevDp: dateUtils.normalizeDate(new Date(response.datePrevDp)),
      datePrevAp: dateUtils.normalizeDate(new Date(response.datePrevAp)),
      lots: response.lots.sort((a, b) => a.id - b.id),
      financements: response.financements.map(financement => ({
        id: financement.financementId,
        budgetEstimer: financement.budgetEstimer,
        monnaieId: financement.monnaieId,
        sourceFinancement: { id: financement.sourceFinancement?.id },
        examenPrealable: financement.examenPrealable,
      })),
      docIds: response.docs.map(doc => doc.id),
      realisationFiles: [],
      comment: response.comment || undefined,
    });
    this.set('initialMilestoneDto', structuredClone(this.get('milestoneDto')));

    const keyMap = {
      serviceIdSelected: 'service',
      typeOfMarketIdSelected: 'typeMarcher',
      selectionModeIdSelected: 'modePassation',
    } as const;

    for (const [storeKey, responseKey] of Object.entries(keyMap)) {
      const item = response[responseKey];
      this.set(storeKey as keyof PpmMilestoneProps, { name: item?.description, code: item?.id });
    }
  }

  removeSlotError(index: number, field: string) {
    const slotErrors = [...(this.get<any>('slotErrors') || [])];

    if (!slotErrors || !slotErrors[index]) return;

    const updatedErrors = [...slotErrors];
    const slotError = { ...updatedErrors[index] };

    slotError[field] = undefined;

    if (Object.keys(slotError).length === 0) {
      updatedErrors.splice(index, 1);
    } else {
      updatedErrors[index] = slotError;
    }
    this.set('slotErrors', updatedErrors);
  }

  removeFundingError(index: number, field: string) {
    const fundingErrors = [...(this.get<any>('fundingErrors') || [])];

    if (!fundingErrors || !fundingErrors[index]) return;

    const updatedErrors = [...fundingErrors];
    const fundingError = { ...updatedErrors[index] };

    fundingError[field] = undefined;

    if (Object.keys(fundingError).length === 0) {
      updatedErrors.splice(index, 1);
    } else {
      updatedErrors[index] = fundingError;
    }
    this.set('fundingErrors', updatedErrors);
  }
}
